// DEPRECATED (2026-07-19): pwa/src/screens/ is scheduled for deletion in STEP-M8-V4-LEGACY-SCREENS.
// Active screens are in pwa/src/v4/screens/ — use those instead.
import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { api, fetchEntities } from "../api/client.js";
import type { PlanHealthData, PhaseInfo, WaveGroup, WaveMilestone, NavigationIntent } from "../api/types.js";
import { PlanHealthHero } from "../components/Heroes.js";
import { ConnectivityBanner } from "../components/ConnectivityBanner.js";

/**
 * Wave-group completion — averaged from each milestone's real step-ratio
 * `completion_pct` (derived server-side from milestone_progress, i.e. actual
 * step states), not from counting milestones whose *stored* status says
 * "complete". Stored milestone status can go stale independently of its
 * steps (confirmed drift: several "complete" milestones still have open
 * steps) — trusting it here would silently inflate the group's progress bar.
 * Falls back to the stored-status count only for milestones the backend
 * couldn't compute a ratio for (completion_pct undefined).
 */
function groupCompletionPct(group: WaveGroup): number {
  const ms = group.milestones ?? [];
  if (ms.length === 0) return 0;
  const total = ms.reduce((sum, m) => {
    const isDone = m.status === "complete" || m.status === "completed";
    if (typeof m.completion_pct === "number") {
      // milestone_progress returns 0 when no steps are linked (e.g. unmigrated
      // historical milestones); honour status instead so completed milestones
      // don't pin the group at 0%.
      return sum + (isDone && m.completion_pct === 0 ? 100 : m.completion_pct);
    }
    return sum + (isDone ? 100 : 0);
  }, 0);
  return Math.round(total / ms.length);
}

/** True completion for a milestone — explicit status wins over computed pct. */
function isMilestoneDone(m: WaveMilestone): boolean {
  if (m.status === "complete" || m.status === "completed") return true;
  if (typeof m.completion_pct === "number") return m.completion_pct === 100;
  return false;
}

export function PhaseTimeline({ phases }: { phases: PhaseInfo[] }) {
  return (
    <div>
      <div className="section-head">
        <h2>Phase History</h2>
        <span className="count">{phases.length}</span>
      </div>
      <div
        style={{
          overflowX: "auto",
          padding: "0 20px 4px",
          scrollbarWidth: "none",
        }}
      >
        <div style={{ display: "flex", gap: 6, minWidth: "max-content" }}>
          {phases.map((phase) => {
            const isComplete =
              phase.status === "complete" || phase.status === "completed";
            const isActive =
              phase.active ||
              phase.status === "active" ||
              phase.status === "in_progress";
            const pct = phase.completion_pct ?? 0;
            return (
              <div
                key={phase.number}
                style={{
                  flexShrink: 0,
                  width: 68,
                  padding: "10px 8px 8px",
                  borderRadius: 10,
                  border: `1px solid ${
                    isActive
                      ? "var(--accent)"
                      : isComplete
                        ? "var(--ok)"
                        : "var(--border-soft)"
                  }`,
                  background: isActive
                    ? "var(--accent-soft)"
                    : isComplete
                      ? "var(--ok-soft)"
                      : "var(--bg-elev)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    fontWeight: 700,
                    color: isActive
                      ? "var(--accent)"
                      : isComplete
                        ? "var(--ok)"
                        : "var(--text-muted)",
                  }}
                >
                  P{phase.number}
                </span>
                {isComplete ? (
                  <span style={{ fontSize: 12, color: "var(--ok)" }}>✓</span>
                ) : isActive ? (
                  <div className="live-dot" style={{ width: 6, height: 6 }} />
                ) : (
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 3,
                      background: "var(--text-dim)",
                    }}
                  />
                )}
                {!isComplete && (
                  <div
                    style={{
                      width: "100%",
                      height: 2,
                      background: "var(--border)",
                      borderRadius: 2,
                    }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: isActive ? "var(--accent)" : "var(--text-dim)",
                        borderRadius: 2,
                      }}
                    />
                  </div>
                )}
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 9,
                    color: isActive ? "var(--accent)" : "var(--text-dim)",
                    textAlign: "center",
                    lineHeight: 1.2,
                  }}
                >
                  {isComplete ? "done" : `${pct}%`}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function milestoneIdFromHash(): string | null {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash.startsWith("roadmap/")) return null;
  return hash.slice("roadmap/".length) || null;
}

export function WaveTimeline({ groups }: { groups: WaveGroup[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedMilestone, setExpandedMilestone] = useState<string | null>(null);
  const [milestoneSteps, setMilestoneSteps] = useState<Record<string, unknown>[]>([]);
  const [stepsLoading, setStepsLoading] = useState(false);
  const [stepsError, setStepsError] = useState(false);
  const currentMilestoneRef = useRef<string | null>(null);

  const expandedGroup = groups.find((g) => g.id === expanded) ?? null;

  const handleMilestoneExpand = useCallback(async (milestoneId: string) => {
    if (expandedMilestone === milestoneId) {
      setExpandedMilestone(null);
      setMilestoneSteps([]);
      return;
    }
    setExpandedMilestone(milestoneId);
    setMilestoneSteps([]);
    setStepsError(false);
    currentMilestoneRef.current = milestoneId;
    setStepsLoading(true);
    try {
      const res = await fetchEntities("steps", { milestoneId, sort: "status" });
      if (currentMilestoneRef.current !== milestoneId) return;
      setMilestoneSteps(res.entities);
    } catch {
      setStepsError(true);
    } finally {
      setStepsLoading(false);
    }
  }, [expandedMilestone]);

  // Auto-expand milestone from hash on mount and on hash navigation
  useEffect(() => {
    const milestoneId = milestoneIdFromHash();
    if (!milestoneId) return;
    const group = groups.find((g) => g.milestones?.some((m) => m.id === milestoneId));
    if (group) setExpanded(group.id);
    void handleMilestoneExpand(milestoneId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only — groups may not be populated yet but state is set for when they arrive

  useEffect(() => {
    function onHashChange() {
      const milestoneId = milestoneIdFromHash();
      if (!milestoneId) return;
      const group = groups.find((g) => g.milestones?.some((m) => m.id === milestoneId));
      if (group) setExpanded(group.id);
      void handleMilestoneExpand(milestoneId);
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [groups, handleMilestoneExpand]);

  return (
    <div>
      <div className="section-head">
        <h2>Wave Groups</h2>
        <span className="count">{groups.length}</span>
      </div>

      <div
        style={{
          overflowX: "auto",
          padding: "0 20px 4px",
          scrollbarWidth: "none",
        }}
      >
        <div style={{ display: "flex", gap: 6, minWidth: "max-content" }}>
          {groups.map((group) => {
            const isComplete =
              group.status === "complete" || group.status === "completed";
            const isActive =
              group.status === "active" || group.status === "in_progress";
            const pct = groupCompletionPct(group);
            const isExp = expanded === group.id;

            return (
              <button
                key={group.id}
                onClick={() => setExpanded(isExp ? null : group.id)}
                style={{
                  flexShrink: 0,
                  width: 68,
                  padding: "10px 8px 8px",
                  borderRadius: 10,
                  border: `1px solid ${
                    isActive
                      ? "var(--accent)"
                      : isComplete
                        ? "var(--ok)"
                        : isExp
                          ? "var(--accent-dim)"
                          : "var(--border-soft)"
                  }`,
                  background: isActive
                    ? "var(--accent-soft)"
                    : isComplete
                      ? "var(--ok-soft)"
                      : isExp
                        ? "var(--bg-elev-2)"
                        : "var(--bg-elev)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    fontWeight: 700,
                    color: isActive
                      ? "var(--accent)"
                      : isComplete
                        ? "var(--ok)"
                        : "var(--text-muted)",
                  }}
                >
                  {group.id}
                </span>

                {group.name && group.name !== group.id && (
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-dim)", textAlign: "center", lineHeight: 1.2, maxWidth: 60, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {group.name}
                  </span>
                )}

                {isComplete ? (
                  <span style={{ fontSize: 12, color: "var(--ok)" }}>✓</span>
                ) : isActive ? (
                  <div className="live-dot" style={{ width: 6, height: 6 }} />
                ) : (
                  <div style={{ width: 6, height: 6, borderRadius: 3, background: "var(--text-dim)" }} />
                )}

                {!isComplete && (
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 8, color: "var(--text-dim)" }}>
                    {group.status || 'pending'}
                  </span>
                )}

                {!isComplete && (
                  <div
                    style={{
                      width: "100%",
                      height: 2,
                      background: "var(--border)",
                      borderRadius: 2,
                    }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: isActive
                          ? "var(--accent)"
                          : "var(--text-dim)",
                        borderRadius: 2,
                      }}
                    />
                  </div>
                )}

                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 9,
                    color: isActive ? "var(--accent)" : "var(--text-dim)",
                  }}
                >
                  {isComplete ? "done" : `${pct}%`}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {expandedGroup && (
        <div style={{ padding: "8px 20px 12px" }}>
          <div
            style={{
              background: "var(--bg-elev)",
              border: "1px solid var(--border-soft)",
              borderRadius: 10,
              padding: "12px 14px",
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "var(--text-muted)",
                marginBottom: (expandedGroup.milestones ?? []).length ? 10 : 0,
              }}
            >
              {expandedGroup.id} — {expandedGroup.name}
            </div>

            {(expandedGroup.milestones ?? []).length === 0 && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--text-dim)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                No milestones
              </div>
            )}

            {(expandedGroup.milestones ?? []).filter((m) => m.id).map((m, i) => {
              const done = isMilestoneDone(m);
              const ms = expandedGroup.milestones ?? [];
              return (
                <Fragment key={m.id}>
                  <div
                    onClick={() => { void handleMilestoneExpand(m.id); }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "7px 0",
                      borderBottom:
                        i < ms.length - 1
                          ? "1px solid var(--border-soft)"
                          : "none",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 3,
                        flexShrink: 0,
                        background: done ? "var(--ok)" : "var(--text-dim)",
                      }}
                    />
                    <span
                      className="mono"
                      style={{
                        color: "var(--accent)",
                        fontSize: 10,
                        flexShrink: 0,
                      }}
                    >
                      {m.id}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        fontSize: 12,
                        color: "var(--text)",
                        lineHeight: 1.3,
                      }}
                    >
                      {m.title || <span style={{ color: "var(--text-dim)", fontSize: 10 }}>{m.id}</span>}
                    </span>
                    <span
                      className={`chip${done ? " ok" : ""}`}
                      style={{ fontSize: 9 }}
                    >
                      {typeof m.completion_pct === "number" && m.completion_pct > 0
                        ? `${m.completion_pct}%`
                        : (m.status || "pending")}
                    </span>
                  </div>
                  {expandedMilestone === m.id && (
                    <div style={{ paddingLeft: 22, paddingBottom: 6 }}>
                      {stepsError ? (
                        <div style={{ fontSize: 11, color: "var(--warn, #f59e0b)", fontFamily: "var(--font-mono)", padding: "4px 0" }}>failed to load steps</div>
                      ) : stepsLoading ? (
                        <div style={{ fontSize: 11, color: "var(--text-dim)", fontFamily: "var(--font-mono)", padding: "4px 0" }}>loading…</div>
                      ) : milestoneSteps.length === 0 ? (
                        <div style={{ fontSize: 11, color: "var(--text-dim)", fontFamily: "var(--font-mono)", padding: "4px 0" }}>no steps</div>
                      ) : (
                        milestoneSteps.map((step, index) => {
                          const stepDone = (step.status as string) === "complete" || (step.status as string) === "completed";
                          return (
                            <div key={step.id as string} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", borderBottom: index < milestoneSteps.length - 1 ? "1px solid var(--border-soft)" : "none" }}>
                              <div style={{ width: 5, height: 5, borderRadius: 3, flexShrink: 0, background: stepDone ? "var(--ok)" : "var(--text-dim)" }} />
                              <span className="mono" style={{ color: "var(--accent)", fontSize: 9, flexShrink: 0 }}>{step.id as string}</span>
                              <span style={{ flex: 1, fontSize: 11, color: "var(--text)", lineHeight: 1.3 }}>{step.title as string}</span>
                              <span className={`chip${stepDone ? " ok" : ""}`} style={{ fontSize: 8 }}>{step.status as string}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

interface RoadmapScreenProps {
  onNavigate?: (intent: string | NavigationIntent) => void;
}

export function RoadmapScreen({ onNavigate }: RoadmapScreenProps) {
  const [data, setData] = useState<PlanHealthData | undefined>(undefined);
  const [waveGroups, setWaveGroups] = useState<WaveGroup[]>([]);
  const [phases, setPhases] = useState<PhaseInfo[]>([]);
  const [loadError, setLoadError] = useState<unknown>(null);

  const load = useCallback(async () => {
    try {
      const [heroes, dashboard] = await Promise.all([
        api.heroes(),
        api.dashboard(),
      ]);
      setData(heroes.planHealth);
      setWaveGroups(dashboard.wave?.groups ?? []);
      setPhases(dashboard.phase?.phases ?? []);
      setLoadError(null);
    } catch (err) {
      setLoadError(err);
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = setInterval(() => void load(), 30_000);
    return () => clearInterval(interval);
  }, [load]);

  if (data === undefined) {
    if (loadError) {
      return <ConnectivityBanner error={loadError} variant="block" />;
    }
    return (
      <div aria-hidden="true">
        <div style={{ background: "var(--bg-elev)", borderBottom: "1px solid var(--border-soft)", padding: "28px 22px 26px" }}>
          <div className="skeleton-block" style={{ height: 11, width: "28%", marginBottom: 16, borderRadius: 4 }} />
          <div className="skeleton-block" style={{ height: 28, width: "85%", marginBottom: 8 }} />
          <div className="skeleton-block" style={{ height: 28, width: "65%", marginBottom: 14 }} />
          <div className="skeleton-block" style={{ height: 13, width: "72%", marginBottom: 20, borderRadius: 4 }} />
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="skeleton-block" style={{ height: 46, width: 76 }} />
            <div className="skeleton-block" style={{ height: 13, width: 56, borderRadius: 4 }} />
          </div>
        </div>
        <div style={{ padding: "18px 20px 8px", display: "flex", alignItems: "baseline", gap: 8 }}>
          <div className="skeleton-block" style={{ height: 11, width: 80, borderRadius: 4 }} />
          <div className="skeleton-block" style={{ height: 18, width: 24, borderRadius: 8 }} />
        </div>
        <div style={{ padding: "0 20px", display: "flex", gap: 6 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton-block" style={{ width: 68, height: 82, borderRadius: 10 }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      {loadError != null && <ConnectivityBanner error={loadError} variant="inline" />}
      <PlanHealthHero data={data} onNavigate={onNavigate} />
      {phases.length > 0 && (
        <PhaseTimeline
          phases={[...new Map(phases.map(p => [p.number, p])).values()]
            .sort((a, b) => a.number - b.number)}
        />
      )}
      {waveGroups.length > 0 && <WaveTimeline groups={waveGroups} />}
      <div style={{ height: 80 }} />
    </>
  );
}
